import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, X, Calendar as CalendarIcon, Upload, Plus, Film, Image, FileText, RefreshCw, Play } from 'lucide-react';
import Sidebar from '../../Sidebar/Sidebar';
import Navbar from '../../Navbars/UserNavbar';
import Modal from "react-responsive-modal";
import "react-responsive-modal/styles.css";
import { toast, ToastContainer } from "react-toastify";
import 'react-toastify/dist/ReactToastify.css';
import { DashboardApi } from '../../../apis/DashboardApi';
import { AxiosResponse } from 'axios';
import { useSelector } from 'react-redux';

interface ScheduledMedia {
  id: number;
  media_id: number;
  recipient_id: number;
  delivery_date: string;
  display_delivery_date?: string;
  formatted_delivery_date?: string;
  calendar_date?: string;
  utc_delivery_date?: string;
  message: string;
  is_legacy: boolean;
  processed: boolean;
  created_at: string;
  updated_at: string;
  userMedia?: {
    file_name: string;
    file_type: string;
    temporaryUrl?: string;
  };
  recipient?: {
    first_name: string;
    last_name: string;
    email: string;
  };
}

interface CalendarEventData {
  [date: string]: {
    mediaCount: number;
    thumbnailUrl: string;
  };
}

interface PaginatedResponse {
  current_page: number;
  data: ScheduledMedia[];
  from: number;
  last_page: number;
  per_page: number;
  to: number;
  total: number;
}

const ScheduleMedia = () => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [scheduleModalOpen, setScheduleModalOpen] = useState(false);
  const [viewScheduleModalOpen, setViewScheduleModalOpen] = useState(false);
  const [demoModalOpen, setDemoModalOpen] = useState(false);
  const [recipients, setRecipients] = useState<any[]>([]);
  const [selectedRecipients, setSelectedRecipients] = useState<string[]>([]);
  const [personalMessage, setPersonalMessage] = useState("");
  const [isLegacy, setIsLegacy] = useState(false);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [files, setFiles] = useState<File[]>([]);
  const [mediaName, setMediaName] = useState("");
  const [mediaDescription, setMediaDescription] = useState("");
  const [mediaType, setMediaType] = useState("image");
  const [calendarEvents, setCalendarEvents] = useState<CalendarEventData>({});
  const [dateSchedules, setDateSchedules] = useState<ScheduledMedia[]>([]);
  const [refreshing, setRefreshing] = useState(false);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [paginatedSchedules, setPaginatedSchedules] = useState<ScheduledMedia[]>([]);
  const [tableLoading, setTableLoading] = useState(false);

  const userLoginData = useSelector((state: any) => state.data.login.value);

  // Updated demo video URL
  const demoVideoUrl = "https://youtu.be/6hQJwLSRMZk?si=s8DLCRVvsdUXqhc2";

  // Get user's timezone
  const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  // Consistent day names
  const daysOfWeek = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

  useEffect(() => {
    fetchRecipients();
    fetchScheduledMedia();
    fetchPaginatedSchedules();
  }, []);

  // Timezone utility functions
  const formatDateForAPI = (date: Date): string => {
    // Simply get the date components and format as YYYY-MM-DD
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    const formattedDate = `${year}-${month}-${day}`;

    return formattedDate;
  };

  const formatDateForDisplay = (date: Date): string => {
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getCurrentDateInUserTimezone = (): Date => {
    // FIXED: More accurate current date detection
    const now = new Date();
    return now;
  };

  const isDateInPast = (date: Date): boolean => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const compareDate = new Date(date);
    compareDate.setHours(0, 0, 0, 0);

    return compareDate < today;
  };

  const refreshAllData = () => {
    // Clear existing events first
    setCalendarEvents({});
    setPaginatedSchedules([]);

    // Fetch fresh data
    fetchScheduledMedia();
    fetchPaginatedSchedules(currentPage);
  };

  const fetchPaginatedSchedules = (page: number = 1) => {
    setTableLoading(true);

    DashboardApi.getLatestScheduledMedia(page).then(
      (response: AxiosResponse<any>) => {
        if (response?.data?.data) {
          const paginatedData = response.data.data as PaginatedResponse;
          setPaginatedSchedules(paginatedData.data);
          setTotalPages(paginatedData.last_page);
          setCurrentPage(paginatedData.current_page);
          setTotalItems(paginatedData.total);
        } else {
          setPaginatedSchedules([]);
          setTotalPages(1);
          setCurrentPage(1);
          setTotalItems(0);
        }
        setTableLoading(false);
      }
    ).catch(error => {
      console.error("Error fetching paginated schedules:", error);
      setPaginatedSchedules([]);
      setTotalPages(1);
      setCurrentPage(1);
      setTotalItems(0);
      setTableLoading(false);
      toast.error("Failed to load scheduled media table");
    });
  };

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      fetchPaginatedSchedules(page);
    }
  };

  const fetchRecipients = () => {
    DashboardApi.getRecipient().then(
      (response: AxiosResponse<any>) => {
        if (response?.data?.data) {
          setRecipients(response.data.data);
        }
      }
    ).catch(error => {
      console.error("Error fetching recipients:", error);
    });
  };

  const fetchScheduledMedia = () => {
    setLoading(true);

    DashboardApi.getScheduledMedia().then(
      (response: AxiosResponse<any>) => {
        if (response?.data?.data) {
          const events: CalendarEventData = {};
          response.data.data.forEach((schedule: ScheduledMedia, index: number) => {
            // Try multiple ways to get the date
            let calendarDate = schedule.calendar_date || schedule.delivery_date;

            // If it's a datetime, extract just the date part
            if (calendarDate && calendarDate.includes('T')) {
              calendarDate = calendarDate.split('T')[0];
            }

            // If it's still not in YYYY-MM-DD format, try to format it
            if (calendarDate && !calendarDate.match(/^\d{4}-\d{2}-\d{2}$/)) {
              const date = new Date(calendarDate);
              if (!isNaN(date.getTime())) {
                calendarDate = formatDateForAPI(date);
              }
            }

            if (calendarDate) {
              if (!events[calendarDate]) {
                events[calendarDate] = {
                  mediaCount: 0,
                  thumbnailUrl: schedule.userMedia?.temporaryUrl || ''
                };
              }

              events[calendarDate].mediaCount += 1;
            } else {
              console.warn("No valid calendar date found for schedule:", schedule.id);
            }
          });

          setCalendarEvents(events);
        } else {
          setCalendarEvents({});
        }
        setLoading(false);
      }
    ).catch(error => {
      console.error("Error fetching scheduled media:", error);
      toast.error("Failed to load calendar data");
      setLoading(false);
      setCalendarEvents({});
    });
  };

  const forceRefresh = () => {
    setRefreshing(true);

    setCalendarEvents({});
    setPaginatedSchedules([]);

    fetchScheduledMedia();
    fetchPaginatedSchedules(1);

    setTimeout(() => {
      setRefreshing(false);
    }, 1000);
  };

  const fetchSchedulesForDate = (date: Date) => {
    const formattedDate = formatDateForAPI(date); // Send YYYY-MM-DD

    DashboardApi.getScheduledMediaByDate(formattedDate).then(
      (response: AxiosResponse<any>) => {
        if (response?.data?.data) {
          setDateSchedules(response.data.data);
        } else {
          setDateSchedules([]);
        }
      }
    ).catch(error => {
      console.error("Error fetching schedules for date:", error);
      toast.error("Failed to load schedules for this date");
      setDateSchedules([]);
    });
  };

  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();

    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const totalDays = lastDay.getDate();

    const daysArray = [];

    let firstDayOfWeek = firstDay.getDay();
    firstDayOfWeek = firstDayOfWeek === 0 ? 6 : firstDayOfWeek - 1;

    for (let i = 0; i < firstDayOfWeek; i++) {
      daysArray.push(null);
    }

    for (let i = 1; i <= totalDays; i++) {
      daysArray.push(i);
    }

    return daysArray;
  };

  const navigateMonth = (direction: 'prev' | 'next') => {
    setCurrentDate(prev => {
      const newDate = new Date(prev);
      if (direction === 'prev') {
        newDate.setMonth(prev.getMonth() - 1);
      } else {
        newDate.setMonth(prev.getMonth() + 1);
      }
      return newDate;
    });
  };

  const navigateYear = (direction: 'prev' | 'next') => {
    setCurrentDate(prev => {
      const newDate = new Date(prev);
      if (direction === 'prev') {
        newDate.setFullYear(prev.getFullYear() - 1);
      } else {
        newDate.setFullYear(prev.getFullYear() + 1);
      }
      return newDate;
    });
  };

  const handleDateSelect = (day: number | null) => {
    if (day === null) return;

    const newDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);

    console.log("Selected date:", newDate);
    console.log("Formatted for display:", formatDateForDisplay(newDate));
    console.log("Formatted for API:", formatDateForAPI(newDate));

    if (isDateInPast(newDate)) {
      toast.info("Please select a future date");
      return;
    }

    setSelectedDate(newDate);

    // Check for events using simple date format
    const calendarDateKey = formatDateForAPI(newDate); // This gives us YYYY-MM-DD

    console.log("Looking for events with key:", calendarDateKey);
    console.log("Available event keys:", Object.keys(calendarEvents));

    if (calendarEvents[calendarDateKey] && calendarEvents[calendarDateKey].mediaCount > 0) {
      console.log("Found existing events, opening view modal");
      fetchSchedulesForDate(newDate);
      setViewScheduleModalOpen(true);
    } else {
      console.log("No events found, opening schedule modal");
      setScheduleModalOpen(true);
    }
  };

  const hasEventsForDate = (day: number | null): boolean => {
    if (!day) return false;

    const date = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
    const calendarDateKey = formatDateForAPI(date); // YYYY-MM-DD format

    return calendarEvents[calendarDateKey] !== undefined;
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = e.target.files;
    if (!selectedFiles) return;

    const filesArray = Array.from(selectedFiles);
    setFiles([...files, ...filesArray]);
  };

  const removeFile = (index: number) => {
    const newFiles = [...files];
    newFiles.splice(index, 1);
    setFiles(newFiles);
  };

  const resetForm = () => {
    setSelectedRecipients([]);
    setPersonalMessage("");
    setIsLegacy(false);
    setFiles([]);
    setMediaName("");
    setMediaDescription("");
  };

  const handleSubmit = async () => {
    if (!selectedDate) {
      toast.error("Please select a date");
      return;
    }

    if (files.length === 0) {
      toast.error("Please upload files");
      return;
    }

    if (!mediaName.trim()) {
      toast.error("Please enter a media name");
      return;
    }

    if (selectedRecipients.length === 0) {
      toast.error("Please select at least one recipient");
      return;
    }

    setUploading(true);

    try {
      const formDataToSend = new FormData();

      if (mediaType === 'image') {
        files.forEach((file, index) => {
          formDataToSend.append('files[]', file);
        });
      } else {
        formDataToSend.append('files', files[0]);
      }

      formDataToSend.append('name', mediaName);
      formDataToSend.append('description', mediaDescription);
      selectedRecipients.forEach((id) => {
        formDataToSend.append('recipients[]', id);
      });
      formDataToSend.append('type', mediaType);
      formDataToSend.append('legacy', isLegacy ? 'on' : 'off');

      const uploadResponse: any = await DashboardApi.UploadMedia(formDataToSend);

      if (uploadResponse?.data?.media_ids && uploadResponse.data.media_ids.length > 0) {
        const mediaIds = uploadResponse.data.media_ids;
        let successCount = 0;

        for (const mediaId of mediaIds) {
          const result = await scheduleMedia(mediaId);
          if (result) successCount++;
        }

        if (successCount > 0) {
          toast.success(`${successCount} media items scheduled successfully`);
          resetForm();
          setScheduleModalOpen(false);
          refreshAllData();
        }
      }
      else if (uploadResponse?.data?.media_id) {
        const result = await scheduleMedia(uploadResponse.data.media_id);
        if (result) {
          toast.success('Media scheduled successfully');
          resetForm();
          setScheduleModalOpen(false);
          refreshAllData();
        }
      }
      else {
        toast.error(uploadResponse?.data?.message);
      }
    } catch (error: any) {
      console.error('Error in scheduling:', error);
      toast.error(error.response?.data?.message || 'Scheduling failed');
    } finally {
      setUploading(false);
    }
  };

  const debugCalendar = () => {
    console.log("=== MANUAL CALENDAR DEBUG ===");
    console.log("Current calendarEvents:", calendarEvents);
    console.log("Available dates:", Object.keys(calendarEvents));
    console.log("Current month/year:", currentDate.getMonth() + 1, currentDate.getFullYear());

    // Check specific dates
    const today = new Date();
    const todayKey = formatDateForAPI(today);
    console.log("Today's key:", todayKey, "Has events:", calendarEvents[todayKey] !== undefined);

    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    const tomorrowKey = formatDateForAPI(tomorrow);
    console.log("Tomorrow's key:", tomorrowKey, "Has events:", calendarEvents[tomorrowKey] !== undefined);

    console.log("=== END MANUAL CALENDAR DEBUG ===");
  };

  const scheduleMedia = async (mediaId: string): Promise<boolean> => {
    try {
      if (!selectedDate) {
        throw new Error("No date selected");
      }

      // Send simple date format
      const formattedDate = formatDateForAPI(selectedDate);
      console?.log(formattedDate)

      const scheduleData = {
        media_id: mediaId,
        recipients: selectedRecipients,
        delivery_date: formattedDate, // Send YYYY-MM-DD only
        message: personalMessage,
        is_legacy: isLegacy
      };

      const response: any = await DashboardApi.scheduleMedia(scheduleData);

      if (response.data.status === 'success') {
        return true;
      } else {
        toast.error(response.data.message || 'Failed to schedule media');
        return false;
      }
    } catch (error: any) {
      console.error('Error in scheduling media:', error);
      toast.error(error.response?.data?.message || 'Scheduling failed');
      return false;
    }
  };

  const deleteScheduledMedia = async (id: number) => {
    try {
      const response: any = await DashboardApi.deleteScheduledMedia(id);

      if (response.data.status === 'success') {
        toast.success('Scheduled media deleted successfully');

        if (selectedDate) {
          fetchSchedulesForDate(selectedDate);
        }

        refreshAllData();
      } else {
        toast.error(response.data.message || 'Failed to delete scheduled media');
      }
    } catch (error: any) {
      console.error('Error deleting scheduled media:', error);
      toast.error(error.response?.data?.message || 'Delete failed');
    }
  };

  const formatDate = (schedule: ScheduledMedia): string => {
    // Use the formatted_delivery_date from backend if available
    if (schedule.formatted_delivery_date) {
      return schedule.formatted_delivery_date;
    }

    // Otherwise format the delivery_date
    if (schedule.delivery_date) {
      const date = new Date(schedule.delivery_date + 'T00:00:00'); // Add time to avoid timezone issues
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    }

    return 'Unknown date';
  };

  return (
    <div className="min-h-screen bg-white">
      <div className="flex flex-col md:flex-row">
        <Sidebar />
        <main className="flex-1 p-6 relative md:ml-64">
          <Navbar />
          <div className="p-6">
            <div className="flex justify-between items-center mb-8">
              <h1 className="text-2xl font-semibold">Schedule Media</h1>

              <div className="flex items-center gap-4">
                {/* Watch Demo Button with Animations */}
                <button
                  onClick={() => setDemoModalOpen(true)}
                  className="group relative px-4 py-2 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-lg font-medium overflow-hidden transform transition-all duration-300 hover:scale-105 hover:shadow-xl focus:outline-none focus:ring-4 focus:ring-orange-300 animate-pulse"
                  style={{
                    animation: 'pulse 2s infinite, glow 3s ease-in-out infinite alternate'
                  }}
                >
                  {/* Animated background */}
                  <div className="absolute inset-0 bg-gradient-to-r from-orange-600 to-orange-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>

                  {/* Shimmer effect */}
                  <div className="absolute inset-0 -skew-x-12 bg-gradient-to-r from-transparent via-white to-transparent opacity-20 group-hover:animate-shimmer"></div>

                  {/* Button content */}
                  <div className="relative flex items-center gap-2">
                    <Play className="w-4 h-4 group-hover:animate-bounce" />
                    <span className="font-semibold text-sm">Watch Demo</span>
                  </div>

                  {/* Floating dots animation */}
                  <div className="absolute -top-1 -right-1 w-3 h-3 bg-yellow-400 rounded-full animate-ping"></div>
                </button>
              </div>
            </div>

            <div className="flex justify-between items-center mb-8">
              <div className="flex items-center gap-2">
                <button
                  onClick={() => navigateMonth('prev')}
                  className="p-1 hover:bg-gray-100 rounded"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <span className="text-lg font-medium">
                  {currentDate.toLocaleString('en-US', { month: 'long', timeZone: userTimezone })}
                </span>
                <button
                  onClick={() => navigateMonth('next')}
                  className="p-1 hover:bg-gray-100 rounded"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={() => navigateYear('prev')}
                  className="p-1 hover:bg-gray-100 rounded"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <span className="text-lg font-medium">
                  {currentDate.getFullYear()}
                </span>
                <button
                  onClick={() => navigateYear('next')}
                  className="p-1 hover:bg-gray-100 rounded"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="grid grid-cols-7 border rounded-lg overflow-hidden">
              {daysOfWeek.map(day => (
                <div
                  key={day}
                  className="p-4 text-sm font-medium text-gray-600 border-b text-center"
                >
                  <span className="hidden md:inline">{day}</span>
                  <span className="md:hidden">{day.slice(0, 3)}</span>
                </div>
              ))}

              {getDaysInMonth(currentDate).map((day, index) => {
                const hasEvents = hasEventsForDate(day);
                const isPastDate = day ? isDateInPast(new Date(currentDate.getFullYear(), currentDate.getMonth(), day)) : false;
                const calendarDateKey = day ? `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}` : '';

                return (
                  <div
                    key={index}
                    onClick={() => handleDateSelect(day)}
                    className={`min-h-[100px] p-2 border relative transition-colors
                        ${!day ? 'bg-gray-50' : isPastDate ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'hover:bg-blue-50 cursor-pointer'}
                        ${hasEvents && !isPastDate ? 'bg-blue-50' : ''}
                      `}
                  >
                    {day && (
                      <>
                        <span className="text-sm">{day}</span>
                        {hasEvents && !isPastDate && (
                          <div className="absolute inset-1 mt-6 bg-blue-600 rounded-lg p-2">
                            <span className="text-white text-xs">
                              {calendarEvents[calendarDateKey].mediaCount} media scheduled
                            </span>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Schedule Table View */}
            <div className="mt-10">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold">Upcoming Scheduled Media</h2>
                <button
                  onClick={() => {
                    const tomorrow = new Date();
                    tomorrow.setDate(tomorrow.getDate() + 1);
                    tomorrow.setHours(12, 0, 0, 0);
                    setSelectedDate(tomorrow);
                    setScheduleModalOpen(true);
                  }}
                  className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
                >
                  <Plus className="w-4 h-4 mr-2 inline" />
                  Schedule New Media
                </button>
              </div>

              {tableLoading ? (
                <div className="text-center py-8">
                  <p className="text-gray-500">Loading scheduled media...</p>
                </div>
              ) : paginatedSchedules.length === 0 ? (
                <div className="text-center py-8 bg-gray-50 rounded-lg">
                  <p className="text-gray-500">No scheduled media found.</p>
                  <p className="text-gray-400 mt-2">Schedule media by selecting a future date on the calendar above.</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full bg-white border rounded-lg overflow-hidden">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="py-3 px-4 text-left text-sm font-medium text-gray-500 uppercase tracking-wider">Media</th>
                        <th className="py-3 px-4 text-left text-sm font-medium text-gray-500 uppercase tracking-wider">Type</th>
                        <th className="py-3 px-4 text-left text-sm font-medium text-gray-500 uppercase tracking-wider">Recipient</th>
                        <th className="py-3 px-4 text-left text-sm font-medium text-gray-500 uppercase tracking-wider">Delivery Date</th>
                        <th className="py-3 px-4 text-left text-sm font-medium text-gray-500 uppercase tracking-wider">Status</th>
                        <th className="py-3 px-4 text-left text-sm font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {paginatedSchedules.map((schedule) => (
                        <tr key={schedule.id} className="hover:bg-gray-50">
                          <td className="py-3 px-4">
                            <div className="text-sm font-medium text-gray-900">
                              {schedule.userMedia?.file_name || 'Unknown media'}
                            </div>
                            {schedule.message && (
                              <div className="text-xs text-gray-500 truncate max-w-xs" title={schedule.message}>
                                Message: {schedule.message}
                              </div>
                            )}
                          </td>
                          <td className="py-3 px-4">
                            <div className="text-sm text-gray-500 capitalize">
                              {schedule.userMedia?.file_type || 'Unknown'}
                            </div>
                          </td>
                          <td className="py-3 px-4">
                            <div className="text-sm text-gray-900">
                              {schedule.recipient ?
                                `${schedule.recipient.first_name} ${schedule.recipient.last_name}`
                                : 'Unknown recipient'}
                            </div>
                            <div className="text-xs text-gray-500">
                              {schedule.recipient?.email || ''}
                            </div>
                          </td>
                          <td className="py-3 px-4">
                            <div className="text-sm text-gray-900">
                              {formatDate(schedule)}
                            </div>
                          </td>
                          <td className="py-3 px-4">
                            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${schedule.processed
                              ? 'bg-green-100 text-green-800'
                              : 'bg-yellow-100 text-yellow-800'}`}>
                              {schedule.processed ? 'Processed' : 'Pending'}
                            </span>
                          </td>
                          <td className="py-3 px-4">
                            {!schedule.processed && (
                              <button
                                onClick={() => deleteScheduledMedia(schedule.id)}
                                className="text-red-500 hover:text-red-700"
                              >
                                Delete
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  {/* Pagination controls */}
                  <div className="mt-4 flex justify-between items-center">
                    <div className="text-sm text-gray-500">
                      Showing {paginatedSchedules.length} of {totalItems} entries
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1 || tableLoading}
                        className="px-3 py-1 rounded bg-gray-200 disabled:opacity-50"
                      >
                        Previous
                      </button>

                      <div className="flex space-x-1">
                        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                          let pageNumber: any;
                          if (totalPages <= 5) {
                            pageNumber = i + 1;
                          } else if (currentPage <= 3) {
                            pageNumber = i + 1;
                          } else if (currentPage >= totalPages - 2) {
                            pageNumber = totalPages - 4 + i;
                          } else {
                            pageNumber = currentPage - 2 + i;
                          }

                          return (
                            <button
                              key={i}
                              onClick={() => handlePageChange(pageNumber)}
                              disabled={tableLoading}
                              className={`px-3 py-1 rounded ${currentPage === pageNumber
                                ? 'bg-blue-500 text-white'
                                : 'bg-gray-200'
                                }`}
                            >
                              {pageNumber}
                            </button>
                          );
                        })}
                      </div>

                      <button
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages || tableLoading}
                        className="px-3 py-1 rounded bg-gray-200 disabled:opacity-50"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </main>
      </div>


      {/* Demo Video Modal */}
      <Modal
        open={demoModalOpen}
        onClose={() => setDemoModalOpen(false)}
        center
        classNames={{
          overlay: 'bg-black bg-opacity-75',
          modal: 'bg-transparent shadow-none max-w-4xl w-full p-0 m-4'
        }}
      >
        <div className="relative bg-black rounded-lg overflow-hidden">
          {/* Close button */}
          <button
            onClick={() => setDemoModalOpen(false)}
            className="absolute top-4 right-4 z-10 bg-black bg-opacity-50 text-white p-2 rounded-full hover:bg-opacity-75 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>

          {/* Video container */}
          <div className="relative w-full h-0" style={{ paddingBottom: '56.25%' }}>
            <video
              src={'https://res.cloudinary.com/dobeex527/video/upload/v1752612568/schedule_media_n27jwl.mov'}
              className="absolute top-0 left-0 w-full h-full object-contain"
              controls
              preload="metadata"
              poster=""
              title="Add Recipients Demo Video"
            >
              Your browser does not support the video tag.
            </video>
          </div>

          {/* Video title */}
          <div className="bg-black text-white p-4">
            <h3 className="text-lg font-semibold">Schedule Media Demo</h3>
            <p className="text-gray-300 text-sm mt-1">
              Learn how to schedule and manage your media content effectively
            </p>
          </div>
        </div>
      </Modal>

      {/* Schedule Media Modal */}
      <Modal open={scheduleModalOpen} onClose={() => setScheduleModalOpen(false)} center>
        <div className="p-6 max-w-2xl w-full">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-semibold">Schedule Media</h2>
          </div>

          <div className="space-y-5">
            <div>
              <h3 className="text-sm font-medium mb-2">Schedule for</h3>
              <div className="p-3 bg-gray-100 rounded-lg flex items-center">
                <CalendarIcon className="w-5 h-5 text-gray-500 mr-2" />
                <span>
                  {selectedDate ? formatDateForDisplay(selectedDate) : 'Select a date'}
                </span>
              </div>
            </div>

            <div>
              <h4 className="text-sm font-medium mb-2">Upload Media</h4>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
                {['image', 'video', 'document'].map((type) => (
                  <button
                    key={type}
                    type="button"
                    onClick={() => setMediaType(type)}
                    className={`p-4 rounded-xl border-2 transition-all capitalize ${mediaType === type
                      ? 'border-blue-500 text-blue-600'
                      : 'border-gray-200'
                      }`}
                  >
                    {type}
                  </button>
                ))}
              </div>

              <div className="space-y-4">
                <input
                  type="text"
                  placeholder="Media Name"
                  value={mediaName}
                  onChange={(e) => setMediaName(e.target.value)}
                  className="w-full p-3 border rounded-lg"
                />

                <textarea
                  placeholder="Description (optional)"
                  value={mediaDescription}
                  onChange={(e) => setMediaDescription(e.target.value)}
                  className="w-full p-3 border rounded-lg"
                  rows={2}
                />

                <div className="border-2 border-dashed rounded-lg p-8">
                  <input
                    type="file"
                    onChange={handleFileChange}
                    className="hidden"
                    id="files"
                    multiple={mediaType === 'image'}
                    accept={mediaType === 'image' ? 'image/*' : undefined}
                  />
                  <label htmlFor="files" className="cursor-pointer block text-center">
                    <div className="mx-auto w-8 h-8 text-gray-400">+</div>
                    <p className="text-center mt-2">
                      {files.length > 0
                        ? `${files.length} file(s) selected`
                        : `Upload ${mediaType}`}
                    </p>
                  </label>
                </div>

                {files.length > 0 && (
                  <div className="mt-2">
                    <h5 className="text-sm font-medium mb-2">Selected Files:</h5>
                    <ul className="space-y-1">
                      {files.map((file, index) => (
                        <li key={index} className="flex justify-between items-center p-2 bg-gray-50 rounded">
                          <span className="truncate">{file.name}</span>
                          <button
                            type="button"
                            onClick={() => removeFile(index)}
                            className="text-red-500"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Recipients</label>
              <select
                name="recipient_select"
                onChange={(e) => {
                  const selectedId = e.target.value;
                  if (selectedId && !selectedRecipients.includes(selectedId)) {
                    setSelectedRecipients([...selectedRecipients, selectedId]);
                  }
                  e.target.value = "";
                }}
                className="w-full p-3 border rounded-lg"
              >
                <option value="">Select Recipients</option>
                {recipients.map((recipient) => (
                  <option
                    key={recipient.id}
                    value={recipient.id}
                    disabled={selectedRecipients.includes(String(recipient.id))}
                  >
                    {recipient.first_name} {recipient.last_name} ({recipient?.group?.name || ''})
                  </option>
                ))}
              </select>

              <div className="flex gap-2 flex-wrap h-20 border-2 border-dashed rounded-lg p-2 mt-2 overflow-auto">
                {selectedRecipients.length === 0 ? (
                  <div className=''>
                    <p className="text-sm text-gray-500 pl-4 pt-3 text-center">No recipients selected </p>
                  </div>
                ) : (
                  selectedRecipients.map((id) => {
                    const selectedRecipient = recipients.find((r) => String(r.id) === id);
                    if (!selectedRecipient) return null;

                    return (
                      <div key={id}>
                        <div
                          className="flex items-center gap-2 px-3 m-1 py-1 bg-gray-400 rounded-full text-sm"
                        >
                          <span>
                            {selectedRecipient.first_name} {selectedRecipient.last_name}
                          </span>
                          <button
                            type="button"
                            onClick={() => {
                              setSelectedRecipients(selectedRecipients.filter(rId => rId !== id));
                            }}
                            className="text-gray-500 hover:text-gray-700"
                          >
                            ×
                          </button>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Personal Message (Optional)</label>
              <textarea
                value={personalMessage}
                onChange={(e) => setPersonalMessage(e.target.value)}
                className="w-full p-3 border rounded-lg"
                rows={3}
                placeholder="Add a personal message to be sent with the media"
              />
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="legacy"
                checked={isLegacy}
                onChange={(e) => setIsLegacy(e.target.checked)}
                className="mr-2"
              />
              <label htmlFor="legacy">Make Legacy</label>
            </div>

            <div className="flex justify-end gap-4">
              <button
                type="button"
                onClick={() => setScheduleModalOpen(false)}
                className="px-6 py-2 text-gray-600"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={uploading}
                className="px-6 py-2 bg-orange-500 text-white rounded-lg disabled:opacity-50"
              >
                {uploading ? 'Scheduling...' : 'Schedule Media'}
              </button>
            </div>
          </div>
        </div>
      </Modal>

      {/* View Scheduled Media Modal */}
      <Modal open={viewScheduleModalOpen} onClose={() => setViewScheduleModalOpen(false)} center>
        <div className="p-6 max-w-3xl w-full">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-semibold">
              Scheduled Media for {selectedDate ? formatDateForDisplay(selectedDate) : ''}
            </h2>
            <div className="flex items-center gap-4">
              <button
                onClick={() => {
                  setViewScheduleModalOpen(false);
                  setScheduleModalOpen(true);
                }}
                className="px-4 py-2 bg-blue-500 text-white rounded-lg"
              >
                Add Media
              </button>
            </div>
          </div>

          {dateSchedules.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-500">No media scheduled for this date.</p>
            </div>
          ) : (<div className="space-y-4">
            {dateSchedules.map((schedule) => (
              <div key={schedule.id} className="border rounded-lg p-4">
                <div className="flex justify-between">
                  <div>
                    <h3 className="font-medium">{schedule.userMedia?.file_name}</h3>
                    <p className="text-sm text-gray-500">Type: {schedule.userMedia?.file_type}</p>
                    <p className="text-sm text-gray-500">
                      Recipient: {schedule.recipient?.first_name} {schedule.recipient?.last_name} ({schedule.recipient?.email})
                    </p>
                    <p className="text-sm text-gray-500">
                      Scheduled for: {schedule.display_delivery_date ?
                        new Date(schedule.display_delivery_date).toLocaleString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                          timeZone: userTimezone
                        }) :
                        formatDate(schedule)
                      }
                    </p>
                    {schedule.message && (
                      <div className="mt-2">
                        <p className="text-sm font-medium">Personal Message:</p>
                        <p className="text-sm text-gray-600">{schedule.message}</p>
                      </div>
                    )}
                    {schedule.is_legacy && (
                      <div className="mt-2">
                        <span className="px-2 py-1 bg-purple-100 text-purple-800 text-xs rounded-full">
                          Legacy Media
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <span className={`px-2 py-1 text-xs rounded-full ${schedule.processed
                      ? 'bg-green-100 text-green-800'
                      : 'bg-yellow-100 text-yellow-800'
                      }`}>
                      {schedule.processed ? 'Processed' : 'Pending'}
                    </span>
                    {!schedule.processed && (
                      <button
                        onClick={() => deleteScheduledMedia(schedule.id)}
                        className="text-red-500 hover:text-red-700 text-sm"
                      >
                        Delete
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
          )}
        </div>
      </Modal>

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />


    </div>
  );
};

export default ScheduleMedia;